import { Component, ElementRef, inject, ViewChild } from "@angular/core";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { AsyncPipe, CommonModule } from "@angular/common";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatListModule } from "@angular/material/list";
import { MatIconModule, MatIconRegistry } from "@angular/material/icon";
import { Router, RouterOutlet, RouterLink } from "@angular/router";
import { Inject, ChangeDetectorRef, NgZone, WritableSignal, signal } from "@angular/core";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { Subject } from "rxjs";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest } from "@azure/msal-browser";
import { filter, takeUntil } from "rxjs/operators";
import { MultilevelMenuService, MultilevelNode, NgMaterialMultilevelMenuModule, ExpandCollapseStatusEnum } from "ng-material-multilevel-menu";
import { PageBasedComponent } from "@app/pages/page.based.component";
import { FooterComponent } from "@shared/components/footer/footer.component";
import { PageRoutePath } from "@app/shared/constants";
import {
  NotChinaKpiSubregionReportMenuItem,
  NotChinaKpiSummaryReportMenuItem,
  NotChinaKpiTerminalReportMenuItem,
  MenuConfig,
  MenuItems,
  OthersMenuItem,
  reportMap,
} from "./side-navigation.constant";
import { DMPRole } from "@app/shared/enums";
import { LoadingSpinnerComponent } from "@app/shared/components/loading-spinner/loading-spinner.component";
import { environment } from "@env/environment";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { IframeReportComponent } from "@app/shared/components/iframe-report/iframe-report.component";

@Component({
  selector: "app-side-navigation",
  templateUrl: "./side-navigation.component.html",
  styleUrls: ["./side-navigation.component.css"],
  standalone: true,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    AsyncPipe,
    RouterOutlet,
    RouterLink,
    TranslateModule,
    NgMaterialMultilevelMenuModule,
    CommonModule,
    LoadingSpinnerComponent,
    IframeReportComponent,
  ],
  providers: [MultilevelMenuService],
})
export class SideNavigationComponent extends PageBasedComponent {
  reportName: string = "";
  reportSheetId: string = "";

  safeSrc: SafeResourceUrl;

  @ViewChild("loadiframe") iframe: ElementRef;
  breakpointObserver = inject(BreakpointObserver);
  // translateService = inject(TranslateService);
  showMenu = false;
  menuWithID: MultilevelNode[] = [] as MultilevelNode[];
  PageRoutePath = PageRoutePath;
  appItems: MultilevelNode[] = [];
  adminItems: any[] = [];
  menuConfig = MenuConfig;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay()
  );

  private readonly _destroying$ = new Subject<void>();

  account: WritableSignal<AccountInfo | null> = signal(null);
  isIframe = false;

  constructor(
    public route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalBroadcastService: MsalBroadcastService,
    private authService: MsalService,
    override translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    public multilevelMenuService: MultilevelMenuService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    public router: Router
  ) {
    super();
    this.safeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(environment.qlik.domain);
    this.matIconRegistry.addSvgIcon("account_balance_red", this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/account_balance_red.svg"));
    this.matIconRegistry.addSvgIcon("admin_panel_settings_red", this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/admin_panel_settings_red.svg"));
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.isIframe = window !== window.parent && !window.opener;

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.account.set(this.authService.instance.getActiveAccount());
        this.cdr.detectChanges();
        // Here is the country from ad domain, but not all users have this property: this.account().idTokenClaims.ctry
      });

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS))
      .subscribe((msg: EventMessage) => localStorage.setItem("tokenExpiration", (msg.payload as any).expiresOn));

    this.msalBroadcastService.msalSubject$.pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)).subscribe((result: EventMessage) => {
      const payload = result.payload as AuthenticationResult;
      this.authService.instance.setActiveAccount(payload.account);
    });

    this.initAppItems(3, "");

    this.profileMe$.subscribe((profileMe) => {
      this.initAppItems(profileMe?.role.role_Id, profileMe?.country);
      this.showMenu = true;
    });

    this.router.events.subscribe((value) => {
      // console.log("router.events %j", this.router.url);
      if (this.router.url.includes("supervisor-dashboard")) {
        this.reportName = this.router.url.substring(this.router.url.lastIndexOf("/") + 1);
        this.reportSheetId = reportMap.get(this.reportName);
      } else {
        this.reportName = "";
        this.reportSheetId = "";
      }
      this.multilevelMenuService.getMatchedObjectByUrl(this.appItems, this.router.url.toString().substring(1));
    });

    this.unMappingTerminals$.pipe(takeUntil(this.destroy$)).subscribe((item) => {
      this.updateAppItem(item && item.length > 0, this.appItems[1], "admin_panel_settings", "admin_panel_settings_red");
      this.appItems[1].items.forEach((appItem) => {
        if (appItem.link === PageRoutePath.ORG_UNIT) {
          this.updateAppItem(item && item.length > 0, appItem, "account_balance", "account_balance_red");
        }
      });
    });
  }

  updateAppItem(isUsingSvgIcon: boolean, appItem: MultilevelNode, iconName: string, svgIconName: string) {
    if (isUsingSvgIcon) {
      appItem.icon = "";
      appItem.svgIcon = svgIconName;
    } else {
      appItem.icon = iconName;
      appItem.svgIcon = "";
    }
  }

  login() {
    if (this.msalGuardConfig.authRequest) this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    else this.authService.loginRedirect();
  }

  logout() {
    this.authService.logoutRedirect();
  }

  viewProfile() {
    // this.ngZone.run(() => this.router.navigate(['profile']));
    this.ngZone.run(() => this.router.navigate(["driver-master"]));
  }

  menuIsReady(menus: MultilevelNode[]) {
    this.menuWithID = menus;
  }

  selectMenuID(MenuID: string) {
    this.multilevelMenuService.selectMenuByID(MenuID);
  }

  setExpandCollapseStatus(type: ExpandCollapseStatusEnum) {
    this.multilevelMenuService.setMenuExpandCollapseStatus(type);
  }

  selectedItem = (event: MultilevelNode) => {
    console.log("event.link %j", event.link);
    this.router.navigate([event.link]);
  };

  initAppItems = (roleId: number, country: string) => {
    var items = MenuItems.slice();
    if (roleId === DMPRole.SUPERVISOR) {
      items.splice(1, 1);
      items.splice(items.length - 2, 0, OthersMenuItem);
    }

    if (country) {
      if (country !== "") {
        if (country !== "CN") {
          if (items[1].items) {
            items[1].items.forEach((menuItem: any) => {
              if (menuItem.items) {
                menuItem.items = menuItem.items.filter((item: any) => !item.link.includes("economy"));
              }
            });
          }
          if (items[2].items) {
            items[2].items[1] = NotChinaKpiSubregionReportMenuItem;
            items[2].items[2] = NotChinaKpiTerminalReportMenuItem;
            items[2].items[3] = NotChinaKpiSummaryReportMenuItem;
          }
        }
      }
    }

    this.translateItems(items);
    this.appItems = items;
  };

  translateItems = (menuItems: MultilevelNode[]) => {
    if (menuItems && menuItems.length > 0) {
      menuItems.forEach((menuItem) => {
        menuItem.label = this.translateService.instant(menuItem.label);
        if (menuItem.items) {
          this.translateItems(menuItem.items);
        }
      });
    }
  };

  blankLink = "about:blank";
  loadLink = environment.qlik.domain;

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    if (typeof Worker !== "undefined") {
      // Create a new
      const worker = new Worker(new URL("./side-navigation.worker", import.meta.url));
      worker.onmessage = ({ data }) => {
        console.log(`${new Date()} page got message: ${data}`);
        this.iframe.nativeElement.src = this.iframe.nativeElement.src === this.blankLink ? this.loadLink : this.blankLink;
        console.log(`${new Date()} refresh iframe src ${this.iframe.nativeElement.src}`);
      };
      worker.postMessage(`refresh iframe src`);
    } else {
      // Web Workers are not supported in this environment.
      // You should add a fallback so that your program still executes correctly.
      window.setInterval(() => {
        console.log(`${new Date()} not use web worker, refresh iframe src ${this.iframe.nativeElement.src}`);
        this.iframe.nativeElement.src = this.iframe.nativeElement.src === this.blankLink ? this.loadLink : this.blankLink;
      }, 1000 * 60 * 12);
    }
  }
}
